/**
 * @file element-font.ts
 * @description Шрифт элемента секции — значение общего поля FontField
 * (phase19.16, партия SB120 «свобода выбора», этап 0; site-builder.md SB120
 * «шрифт — любой из каталога и своих», SB10 роли шрифтов; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §4).
 *
 * Здесь — только значение, пределы и чистые функции. С секции v4 (SB120
 * этап 3а, db-design-sb120-stage3 §2) значение живёт в части `typo.<часть>`
 * элемента (element-typo.ts `typoPartSchema`):
 *   - `font` — `ElementFont`: роль сайта или свой шрифт (ключ каталога или
 *     `shop:<id>`); «Авто» (`role: "auto"`) в базу не пишется — его снимает
 *     `compactTypo`;
 *   - `weight?`, `case?`, `size?`, `tracking?` — нет ключа = «как у роли»
 *     (начертание и регистр — из темы, размер — из уровня H1–H3 и шкалы).
 *
 * Роли шрифта элемента (`TEXT_FONT_ROLES`) живут здесь, а не в schema.ts:
 * schema.ts импортирует element-typo.ts → этот файл, и обратный импорт дал
 * бы цикл с обращением к ещё не созданной константе. Подсчёт семейств
 * (`countFontFamilies`, `themeFontKeys`) переехал в element-typo.ts — ему
 * нужны части `typo` секций.
 *
 * Свой шрифт хранится ключом, а не именем: смена реестра (удаление своего
 * шрифта магазином) не ломает данные — на сайте тогда шрифт роли
 * (`elementFontKey` с реестром).
 */

import { z } from "zod";

import { effectiveFontKey, type FontPartRole, type FontRoleTheme } from "../site-theme/font-roles.js";
import { isShopFontKey, isSiteFontKey, type SiteFontRegistry } from "../site-theme/fonts.js";

/**
 * Шрифт элемента по роли (SB10): auto — по роли части элемента
 * (font-roles.ts), иначе — шрифт роли темы.
 */
export const TEXT_FONT_ROLES = ["auto", "heading", "body", "accent"] as const;
export type TextFontRole = (typeof TEXT_FONT_ROLES)[number];
export const TEXT_FONT_ROLE_LABEL = "Шрифт";
export const TEXT_FONT_ROLE_OPTIONS: Readonly<Record<TextFontRole, string>> = {
  auto: "По роли",
  heading: "Заголовков",
  body: "Основного текста",
  accent: "Акцента",
};

/** Пределы настроек шрифта элемента (SB120: «числом в разумных пределах»). */
export const ELEMENT_FONT_LIMITS = {
  /** Начертание: 100–900 шагом 100 (как `theme.type.headingWeight`). */
  weight: { min: 100, max: 900, step: 100 },
  /**
   * Размер текста, px: 10–96, дроби без шага (db-design-sb120-stage3 §2,
   * расхождение 8): подъём v3 → v4 даёт 17,9375 px, округление сдвинуло бы
   * пиксель. Кабинет ограничивает ввод двумя знаками.
   */
  size: { min: 10, max: 96 },
  /** Разрядка, em: 0–0,4 шагом 0,01. */
  tracking: { min: 0, max: 0.4, step: 0.01 },
  /** Длина ключа шрифта: `shop:` + cuid до 33 знаков — с запасом. */
  keyMax: 64,
} as const;

/** Регистр элемента: как написано или прописными (как `theme.type.headingCase`). */
export const ELEMENT_FONT_CASES = ["asis", "upper"] as const;
export type ElementFontCase = (typeof ELEMENT_FONT_CASES)[number];

/** Ключ шрифта, допустимый у элемента: каталог платформы или свой шрифт `shop:<cuid>`. */
export function isElementFontKey(key: string): boolean {
  return isSiteFontKey(key) || isShopFontKey(key);
}

/**
 * Шрифт элемента: роль сайта или свой шрифт. Варианты строгие: внутри строгой
 * схемы v4 лишний ключ в `typo.<часть>.font` — ошибка, а не молчаливый отброс.
 */
export const elementFontSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), role: z.enum(TEXT_FONT_ROLES) }),
  z.strictObject({
    kind: z.literal("custom"),
    // Ключ каталога или `shop:<id>`; есть ли такой шрифт у магазина — решает реестр.
    font: z.string().min(1).max(ELEMENT_FONT_LIMITS.keyMax).refine(isElementFontKey, "Неизвестный ключ шрифта"),
  }),
]);
export type ElementFont = z.infer<typeof elementFontSchema>;

/** Умолчание: «как у сайта» — шрифт по роли части элемента. */
export const DEFAULT_ELEMENT_FONT: ElementFont = { kind: "role", role: "auto" };

/** Число с шагом 0,01 (без хвостов плавающей точки). */
const isHundredth = (v: number): boolean => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6;

const L = ELEMENT_FONT_LIMITS;

/**
 * Необязательные поля рядом с `font` — кирпич части `typo` (element-typo.ts
 * `typoPartSchema`).
 */
export const elementFontStyleFields = {
  weight: z.number().int().min(L.weight.min).max(L.weight.max).multipleOf(L.weight.step).optional(),
  case: z.enum(ELEMENT_FONT_CASES).optional(),
  size: z.number().finite().min(L.size.min).max(L.size.max).optional(),
  tracking: z.number().min(L.tracking.min).max(L.tracking.max).refine(isHundredth, "Шаг разрядки — 0,01 em").optional(),
};

/** Настройки начертания элемента; нет ключа — «как у роли». */
export interface ElementFontStyle {
  weight?: number;
  case?: ElementFontCase;
  size?: number;
  tracking?: number;
}

export interface ElementFontKeyOptions {
  /** Роль части элемента для `auto` (font-roles.ts `elementFontParts`); нет — основной текст. */
  partRole?: FontPartRole;
  /** Реестр магазина: свой шрифт, которого в нём нет (удалён), заменяется шрифтом роли. */
  registry?: SiteFontRegistry;
}

/**
 * Ключ шрифта, который стоит у элемента на сайте: роль — через
 * `effectiveFontKey` (капс по `eyebrowFrom`, акцент без шрифта → заголовков),
 * свой — его ключ, если он есть в реестре (реестр не передан — без проверки).
 */
export function elementFontKey(value: ElementFont, theme: FontRoleTheme, options: ElementFontKeyOptions = {}): string {
  const partRole = options.partRole ?? "body";
  const roleKey = (role: FontPartRole): string => effectiveFontKey(role, theme).key;
  if (value.kind === "custom") {
    if (!options.registry || options.registry.has(value.font)) return value.font;
    return roleKey(partRole);
  }
  return roleKey(value.role === "auto" ? partRole : value.role);
}
